import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  Plus, ClipboardList, CheckCircle, Clock, Send, Save,
  ChevronDown, ChevronUp, Star, AlertTriangle, Calendar,
  FileText, MessageSquare, User, Award, X
} from 'lucide-react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import './PerformanceReview.css';

// Review categories and questions matching the Two Sisters form
const REVIEW_CATEGORIES = [
  {
    key: 'reliability',
    title: 'Reliability & Attendance',
    subtitle: 'We depend on each other. Showing up consistently and on time makes everything else possible.',
    maxScore: 25,
    items: [
      { key: 'onTime', label: 'Reports to work on time for scheduled shifts' },
      { key: 'attendance', label: 'Maintains acceptable attendance' },
      { key: 'communicates', label: 'Communicates schedule conflicts in advance (24+ hours notice)' },
      { key: 'reliable', label: 'Available and reliable for assigned jobs' },
      { key: 'breaks', label: 'Returns from breaks on time' },
    ]
  },
  {
    key: 'quality',
    title: 'Quality of Work',
    subtitle: 'Our reputation is built on every home we touch. Details matter.',
    maxScore: 25,
    items: [
      { key: 'checklists', label: 'Follows cleaning checklists thoroughly' },
      { key: 'detail', label: 'Demonstrates attention to detail' },
      { key: 'standards', label: 'Completes all tasks to company standards' },
      { key: 'pride', label: 'Takes pride in finished results' },
      { key: 'clientFeedback', label: 'Client feedback rating (if applicable)' },
    ]
  },
  {
    key: 'professionalism',
    title: 'Professionalism',
    subtitle: 'We represent Two Sisters in every interaction—with clients, teammates, and in clients\' homes.',
    maxScore: 20,
    items: [
      { key: 'phonePolicy', label: 'Follows phone/device policy (zero tolerance)' },
      { key: 'clientComm', label: 'Maintains appropriate communication with clients' },
      { key: 'respect', label: 'Respects client property and privacy' },
      { key: 'uniform', label: 'Adheres to uniform and appearance standards' },
    ]
  },
  {
    key: 'teamwork',
    title: 'Teamwork',
    subtitle: 'We work better together. Support your teammates and they\'ll support you.',
    maxScore: 15,
    items: [
      { key: 'collaborates', label: 'Collaborates effectively with coworkers' },
      { key: 'helps', label: 'Willing to help others when needed' },
      { key: 'feedback', label: 'Accepts feedback constructively' },
    ]
  },
  {
    key: 'efficiency',
    title: 'Efficiency & Initiative',
    subtitle: 'Work smarter, stay focused, and take ownership of your time.',
    maxScore: 15,
    items: [
      { key: 'timeframes', label: 'Completes jobs within expected timeframes' },
      { key: 'focused', label: 'Stays focused during shifts (no distractions)' },
      { key: 'proactive', label: 'Identifies and communicates problems proactively' },
    ]
  }
];

const SCORE_LABELS = {
  5: 'Exceptional',
  4: 'Exceeds',
  3: 'Meets',
  2: 'Needs Improvement',
  1: 'Unsatisfactory'
};

const LEVEL_LABELS = [
  { min: 90, label: 'Exceptional', color: '#059669' },
  { min: 80, label: 'Exceeds Expectations', color: '#2563eb' },
  { min: 70, label: 'Meets Expectations', color: '#d97706' },
  { min: 60, label: 'Needs Improvement', color: '#ea580c' },
  { min: 0, label: 'Performance Plan Required', color: '#dc2626' },
];

function getLevel(score) {
  return LEVEL_LABELS.find(l => score >= l.min) || LEVEL_LABELS[LEVEL_LABELS.length - 1];
}

function calcTotal(scores) {
  if (!scores) return 0;
  return Object.values(scores).reduce((sum, val) => sum + (val || 0), 0);
}

// ===== SCORE INPUT ROW =====
function ScoreRow({ item, value, onChange, disabled, compareValue }) {
  return (
    <div className="review-score-row">
      <div className="score-label">{item.label}</div>
      <div className="score-inputs">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            className={`score-btn ${value === n ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && onChange(n)}
            disabled={disabled}
            title={SCORE_LABELS[n]}
          >
            {n}
          </button>
        ))}
        {compareValue !== undefined && compareValue !== null && (
          <span className="score-compare" title="Other's score">{compareValue}</span>
        )}
      </div>
    </div>
  );
}

// ===== REVIEW FORM (used by both employee and admin) =====
function ReviewForm({ scores, setScores, strengths, setStrengths, growth, setGrowth, goals, setGoals, comments, setComments, disabled, compareScores, showComments }) {
  const [expandedCat, setExpandedCat] = useState(REVIEW_CATEGORIES.map(c => c.key));

  const toggleCat = (key) => {
    setExpandedCat(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  return (
    <div className="review-form">
      {REVIEW_CATEGORIES.map(cat => {
        const isOpen = expandedCat.includes(cat.key);
        const catScore = cat.items.reduce((s, item) => s + (scores?.[item.key] || 0), 0);
        const catComplete = cat.items.every(item => scores?.[item.key] > 0);

        return (
          <div key={cat.key} className={`review-category ${isOpen ? 'open' : ''}`}>
            <div className="review-cat-header" onClick={() => toggleCat(cat.key)}>
              <div className="review-cat-left">
                <h3>{cat.title}</h3>
                <p className="review-cat-sub">{cat.subtitle}</p>
              </div>
              <div className="review-cat-right">
                <span className={`review-cat-score ${catComplete ? 'complete' : ''}`}>
                  {catScore} / {cat.maxScore}
                </span>
                {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
            </div>
            {isOpen && (
              <div className="review-cat-body">
                {cat.items.map(item => (
                  <ScoreRow
                    key={item.key}
                    item={item}
                    value={scores?.[item.key] || 0}
                    onChange={(val) => setScores(prev => ({ ...prev, [item.key]: val }))}
                    disabled={disabled}
                    compareValue={compareScores?.[item.key]}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="review-text-sections">
        <div className="review-text-group">
          <label><Award size={16} /> Strengths</label>
          <textarea
            value={strengths}
            onChange={e => setStrengths(e.target.value)}
            placeholder="What does this person do well?"
            rows={3}
            disabled={disabled}
          />
        </div>
        <div className="review-text-group">
          <label><AlertTriangle size={16} /> Areas for Growth</label>
          <textarea
            value={growth}
            onChange={e => setGrowth(e.target.value)}
            placeholder="Where can they improve?"
            rows={3}
            disabled={disabled}
          />
        </div>
        <div className="review-text-group">
          <label><Star size={16} /> Goals for Next 90 Days</label>
          <textarea
            value={goals}
            onChange={e => setGoals(e.target.value)}
            placeholder="What should they work on?"
            rows={3}
            disabled={disabled}
          />
        </div>
        {showComments && (
          <div className="review-text-group">
            <label><MessageSquare size={16} /> Additional Comments</label>
            <textarea
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="Any additional comments..."
              rows={3}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ===== MAIN COMPONENT: Employee Detail Reviews Section =====
export function EmployeeReviewSection({ employee }) {
  const { createReview, updateReview, getEmployeeReviews } = useApp();
  const [activeReviewId, setActiveReviewId] = useState(null);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  // Admin scores state
  const [adminScores, setAdminScores] = useState({});
  const [adminStrengths, setAdminStrengths] = useState('');
  const [adminGrowth, setAdminGrowth] = useState('');
  const [adminGoals, setAdminGoals] = useState('');
  const [adminComments, setAdminComments] = useState('');

  // Discussion state
  const [discussionDate, setDiscussionDate] = useState('');
  const [discussionNotes, setDiscussionNotes] = useState('');
  const [discussionActions, setDiscussionActions] = useState('');

  const reviews = useMemo(() => getEmployeeReviews(employee.id), [employee.id, getEmployeeReviews]);
  const activeReview = reviews.find(r => r.id === activeReviewId);

  // Days since hire
  const daysSinceHire = useMemo(() => {
    if (!employee.hireDate) return null;
    try {
      return differenceInCalendarDays(new Date(), parseISO(employee.hireDate));
    } catch { return null; }
  }, [employee.hireDate]);

  const needsReview = daysSinceHire !== null && daysSinceHire >= 80 && reviews.length === 0;
  const pastDue = daysSinceHire !== null && daysSinceHire >= 90 && !reviews.some(r => r.status === 'completed');

  // Create review as draft — no email sent
  const handleStartReview = async () => {
    setSaving(true);
    try {
      const review = await createReview(employee.id);
      handleLoadReview(review);
      setShowStartConfirm(false);
    } catch (e) {
      console.error('Failed to create review:', e);
      alert('Failed to start review. Please try again.');
    }
    setSaving(false);
  };

  // Send self-review email to employee
  const handleSendToEmployee = async () => {
    if (!activeReview) return;
    setSaving(true);
    try {
      await updateReview(activeReview.id, { status: 'pending_employee' });

      if (employee.email) {
        try {
          await fetch('/.netlify/functions/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'raw',
              data: {
                to: employee.email,
                subject: '90-Day Performance Review - Self Assessment',
                html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #2c5f4e;">Your 90-Day Review</h2>
                  <p>Hi ${employee.firstName},</p>
                  <p>It's time for your 90-day performance review! Please complete your self-assessment in the team app.</p>
                  <p>Log in to the team portal and look for the review notification to get started.</p>
                  <p style="margin-top: 24px; color: #6b7280; font-size: 14px;">— Two Sisters Cleaning</p>
                </div>`
              }
            })
          });
        } catch (e) { console.error('Email failed:', e); }
      }
    } catch (e) {
      console.error('Failed to send to employee:', e);
      alert('Failed to send. Please try again.');
    }
    setSaving(false);
  };

  const handleLoadReview = (review) => {
    setActiveReviewId(review.id);
    setAdminScores(review.adminScores || {});
    setAdminStrengths(review.adminStrengths || '');
    setAdminGrowth(review.adminGrowth || '');
    setAdminGoals(review.adminGoals || '');
    setAdminComments(review.adminComments || '');
    setDiscussionDate(review.discussionDate || '');
    setDiscussionNotes(review.discussionNotes || '');
    setDiscussionActions(review.discussionActionItems || '');
  };

  const handleSaveAdminReview = async (complete = false) => {
    if (!activeReview) return;
    setSaving(true);
    try {
      const updates = {
        adminScores,
        adminStrengths,
        adminGrowth,
        adminGoals,
        adminComments,
        discussionDate: discussionDate || null,
        discussionNotes,
        discussionActionItems: discussionActions
      };
      if (complete) {
        updates.status = 'completed';
        updates.adminCompletedAt = new Date().toISOString();
        updates.completedAt = new Date().toISOString();
      }
      await updateReview(activeReview.id, updates);

      if (complete && employee.email) {
        // Send completed review notification
        try {
          await fetch('/.netlify/functions/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'raw',
              data: {
                to: employee.email,
                subject: '90-Day Review Complete - Two Sisters Cleaning',
                html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #2c5f4e;">Your 90-Day Review is Complete</h2>
                  <p>Hi ${employee.firstName},</p>
                  <p>Your 90-day performance review has been completed and discussed. A copy is available for your records.</p>
                  <p><strong>Overall Score:</strong> ${calcTotal(adminScores)} / 100</p>
                  <p><strong>Discussion Date:</strong> ${discussionDate ? format(parseISO(discussionDate), 'MMMM d, yyyy') : 'TBD'}</p>
                  ${discussionActions ? `<p><strong>Action Items:</strong> ${discussionActions}</p>` : ''}
                  <p style="margin-top: 24px; color: #6b7280; font-size: 14px;">— Two Sisters Cleaning</p>
                </div>`
              }
            })
          });
        } catch (e) { console.error('Email failed:', e); }
      }

      if (complete) setActiveReviewId(null);
      if (!complete) {
        setSavedMsg(true);
        setTimeout(() => setSavedMsg(false), 2000);
      }
    } catch (e) {
      console.error('Failed to save review:', e);
      alert('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const empTotal = activeReview ? calcTotal(activeReview.employeeScores) : 0;
  const admTotal = calcTotal(adminScores);
  const empLevel = getLevel(empTotal);
  const admLevel = getLevel(admTotal);

  return (
    <div className="review-section">
      <div className="review-section-header">
        <h3><ClipboardList size={18} /> Performance Reviews</h3>
        {(needsReview || pastDue) && (
          <span className={`review-flag ${pastDue ? 'overdue' : 'upcoming'}`}>
            <AlertTriangle size={14} />
            {pastDue ? `${daysSinceHire} days — review overdue!` : `${daysSinceHire} days — review coming up`}
          </span>
        )}
      </div>

      {/* Review History */}
      {reviews.length > 0 && !activeReview && (
        <div className="review-history">
          {reviews.map(r => (
            <div key={r.id} className={`review-history-card ${r.status}`} onClick={() => handleLoadReview(r)}>
              <div className="review-history-left">
                <span className="review-history-type">90-Day Review</span>
                <span className="review-history-date">{r.createdAt ? format(parseISO(r.createdAt), 'MMM d, yyyy') : ''}</span>
              </div>
              <div className="review-history-right">
                <span className={`review-status-badge ${r.status}`}>
                  {r.status === 'draft' && <><FileText size={12} /> Draft</>}
                  {r.status === 'pending_employee' && <><Clock size={12} /> Waiting on Employee</>}
                  {r.status === 'pending_admin' && <><ClipboardList size={12} /> Ready for Admin</>}
                  {r.status === 'completed' && <><CheckCircle size={12} /> Completed ({calcTotal(r.adminScores)}/100)</>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Start Review Button */}
      {!activeReview && (
        <div className="review-start-area">
          {showStartConfirm ? (
            <div className="review-confirm-box">
              <p>This will create a new 90-day review for <strong>{employee.firstName}</strong>. You can fill in your scores, save as a draft, and send the self-assessment to the employee whenever you're ready.</p>
              <div className="review-confirm-actions">
                <button className="btn-review-start" onClick={handleStartReview} disabled={saving}>
                  {saving ? 'Creating...' : 'Create Review'}
                </button>
                <button className="btn-review-cancel" onClick={() => setShowStartConfirm(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="btn-review-new" onClick={() => setShowStartConfirm(true)}>
              <Plus size={18} /> Start 90-Day Review
            </button>
          )}
        </div>
      )}

      {/* Active Review */}
      {activeReview && (
        <div className="review-active">
          <div className="review-active-header">
            <div>
              <h3>90-Day Review — {employee.firstName} {employee.lastName}</h3>
              <span className={`review-status-badge ${activeReview.status}`}>
                {activeReview.status === 'draft' && <><FileText size={12} /> Draft</>}
                {activeReview.status === 'pending_employee' && <><Clock size={12} /> Waiting on Employee</>}
                {activeReview.status === 'pending_admin' && <><ClipboardList size={12} /> Employee Done — Your Turn</>}
                {activeReview.status === 'completed' && <><CheckCircle size={12} /> Completed</>}
              </span>
            </div>
            <button className="btn-review-close" onClick={() => setActiveReviewId(null)}><X size={18} /></button>
          </div>

          {/* Pending employee notice */}
          {activeReview.status === 'pending_employee' && (
            <div className="review-info-banner waiting">
              <Clock size={16} />
              <span>{employee.firstName} hasn't completed their self-review yet. You can continue working on your scores below.</span>
            </div>
          )}

          {/* Score Summary — show whenever there are any scores */}
          {(empTotal > 0 || admTotal > 0) && (
            <div className="review-score-summary">
              <div className="score-summary-card employee">
                <span className="score-summary-label"><User size={14} /> Employee Self-Score</span>
                <span className="score-summary-value">{empTotal > 0 ? `${empTotal} / 100` : '—'}</span>
                {empTotal > 0 && <span className="score-summary-level" style={{ color: empLevel.color }}>{empLevel.label}</span>}
              </div>
              <div className="score-summary-card admin">
                <span className="score-summary-label"><ClipboardList size={14} /> Admin Score</span>
                <span className="score-summary-value">{admTotal > 0 ? `${admTotal} / 100` : '—'}</span>
                {admTotal > 0 && <span className="score-summary-level" style={{ color: admLevel.color }}>{admLevel.label}</span>}
              </div>
            </div>
          )}

          {/* Show employee self-assessment if they've completed it */}
          {activeReview.employeeScores && (activeReview.status === 'pending_admin' || activeReview.status === 'completed') && (
            <div className="review-column employee-col" style={{ marginBottom: 16 }}>
              <h4><User size={16} /> {employee.firstName}'s Self-Assessment</h4>
              <ReviewForm
                scores={activeReview.employeeScores || {}}
                setScores={() => {}}
                strengths={activeReview.employeeStrengths || ''}
                setStrengths={() => {}}
                growth={activeReview.employeeGrowth || ''}
                setGrowth={() => {}}
                goals={activeReview.employeeGoals || ''}
                setGoals={() => {}}
                comments={activeReview.employeeComments || ''}
                setComments={() => {}}
                disabled={true}
                showComments={true}
              />
            </div>
          )}

          {/* Admin review form — always available except when completed */}
          <div className="review-column admin-col" style={{ marginBottom: 16 }}>
            <h4><ClipboardList size={16} /> Your Review</h4>
            <ReviewForm
              scores={adminScores}
              setScores={setAdminScores}
              strengths={adminStrengths}
              setStrengths={setAdminStrengths}
              growth={adminGrowth}
              setGrowth={setAdminGrowth}
              goals={adminGoals}
              setGoals={setAdminGoals}
              comments={adminComments}
              setComments={setAdminComments}
              disabled={activeReview.status === 'completed'}
              compareScores={activeReview.employeeScores}
              showComments={true}
            />
          </div>

          {/* Discussion Section */}
          <div className="review-discussion">
            <h4><MessageSquare size={16} /> Discussion</h4>
            <div className="discussion-fields">
              <div className="discussion-field">
                <label>Discussion Date</label>
                <input
                  type="date"
                  value={discussionDate}
                  onChange={e => setDiscussionDate(e.target.value)}
                  disabled={activeReview.status === 'completed'}
                />
              </div>
              <div className="discussion-field full">
                <label>Discussion Notes</label>
                <textarea
                  value={discussionNotes}
                  onChange={e => setDiscussionNotes(e.target.value)}
                  placeholder="What was discussed during the review meeting?"
                  rows={4}
                  disabled={activeReview.status === 'completed'}
                />
              </div>
              <div className="discussion-field full">
                <label>Action Items</label>
                <textarea
                  value={discussionActions}
                  onChange={e => setDiscussionActions(e.target.value)}
                  placeholder="Agreed-upon action items and next steps..."
                  rows={3}
                  disabled={activeReview.status === 'completed'}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {activeReview.status !== 'completed' && (
            <div className="review-actions">
              <button className="btn-review-save" onClick={() => handleSaveAdminReview(false)} disabled={saving}>
                <Save size={16} /> {saving ? 'Saving...' : savedMsg ? '✓ Saved!' : 'Save Draft'}
              </button>

              {/* Send to Employee — only show if not yet sent */}
              {activeReview.status === 'draft' && (
                <button
                  className="btn-review-send"
                  onClick={() => {
                    if (window.confirm(`Send the self-assessment to ${employee.firstName}? They'll get an email to complete their part.`)) {
                      handleSendToEmployee();
                    }
                  }}
                  disabled={saving}
                >
                  <Send size={16} /> Send Self-Review to Employee
                </button>
              )}

              {/* Finalize — only show once admin has scored */}
              {(activeReview.status === 'pending_admin' || activeReview.status === 'pending_employee' || activeReview.status === 'draft') && (
                <button
                  className="btn-review-complete"
                  onClick={() => {
                    if (admTotal === 0) {
                      alert('Please complete your scores before finalizing.');
                      return;
                    }
                    if (window.confirm('Finalize this review? This will email the completed review to the employee.')) {
                      handleSaveAdminReview(true);
                    }
                  }}
                  disabled={saving}
                >
                  <CheckCircle size={16} /> Finalize & Email to Employee
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== TEAM APP: Employee Self-Review =====
export function EmployeeSelfReview({ review, employee, onComplete }) {
  const { updateReview } = useApp();
  const [scores, setScores] = useState(review.employeeScores || {});
  const [strengths, setStrengths] = useState(review.employeeStrengths || '');
  const [growth, setGrowth] = useState(review.employeeGrowth || '');
  const [goals, setGoals] = useState(review.employeeGoals || '');
  const [comments, setComments] = useState(review.employeeComments || '');
  const [saving, setSaving] = useState(false);

  const total = calcTotal(scores);
  const allScored = REVIEW_CATEGORIES.every(cat => cat.items.every(item => scores[item.key] > 0));

  const handleSubmit = async () => {
    if (!allScored) {
      alert('Please score all items before submitting.');
      return;
    }
    if (!window.confirm('Submit your self-assessment? You won\'t be able to change it after.')) return;
    
    setSaving(true);
    try {
      await updateReview(review.id, {
        employeeScores: scores,
        employeeStrengths: strengths,
        employeeGrowth: growth,
        employeeGoals: goals,
        employeeComments: comments,
        employeeCompletedAt: new Date().toISOString(),
        status: 'pending_admin'
      });
      if (onComplete) onComplete();
    } catch (e) {
      console.error('Failed to submit review:', e);
      alert('Failed to submit. Please try again.');
    }
    setSaving(false);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await updateReview(review.id, {
        employeeScores: scores,
        employeeStrengths: strengths,
        employeeGrowth: growth,
        employeeGoals: goals,
        employeeComments: comments
      });
      alert('Draft saved!');
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
    setSaving(false);
  };

  const level = getLevel(total);

  return (
    <div className="self-review-container">
      <div className="self-review-header">
        <h2>90-Day Self-Assessment</h2>
        <p>Rate yourself honestly on each item (1-5). Your manager will also complete their own review, and you'll discuss together.</p>
        <div className="self-review-total">
          <span className="self-review-total-score">{total} / 100</span>
          {total > 0 && <span className="self-review-total-level" style={{ color: level.color }}>{level.label}</span>}
        </div>
      </div>

      <ReviewForm
        scores={scores}
        setScores={setScores}
        strengths={strengths}
        setStrengths={setStrengths}
        growth={growth}
        setGrowth={setGrowth}
        goals={goals}
        setGoals={setGoals}
        comments={comments}
        setComments={setComments}
        disabled={false}
        showComments={true}
      />

      <div className="self-review-actions">
        <button className="btn-self-save" onClick={handleSaveDraft} disabled={saving}>
          <Save size={16} /> Save Draft
        </button>
        <button className="btn-self-submit" onClick={handleSubmit} disabled={saving || !allScored}>
          <Send size={16} /> {saving ? 'Submitting...' : 'Submit Self-Assessment'}
        </button>
      </div>
    </div>
  );
}

// ===== DASHBOARD FLAG: Employees needing reviews =====
export function ReviewAlerts() {
  const { state } = useApp();
  
  const alerts = useMemo(() => {
    return state.employees
      .filter(e => e.status === 'active' && e.hireDate)
      .map(e => {
        let days;
        try { days = differenceInCalendarDays(new Date(), parseISO(e.hireDate)); } catch { return null; }
        const hasReview = state.reviews.some(r => r.employeeId === e.id);
        if (days >= 80 && !hasReview) {
          return { employee: e, days, overdue: days >= 90 };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.days - a.days);
  }, [state.employees, state.reviews]);

  if (alerts.length === 0) return null;

  return (
    <div className="review-alerts-card">
      <h4><ClipboardList size={16} /> Reviews Due</h4>
      {alerts.map(a => (
        <div key={a.employee.id} className={`review-alert-row ${a.overdue ? 'overdue' : ''}`}>
          <span className="review-alert-name">{a.employee.firstName} {a.employee.lastName}</span>
          <span className="review-alert-days">
            {a.overdue ? <AlertTriangle size={12} /> : <Clock size={12} />}
            Day {a.days}
          </span>
        </div>
      ))}
    </div>
  );
}

export default EmployeeReviewSection;
